import {
  Actions,
  Async,
  Dispatchers,
  F0,
  toAsyncFetched,
  TypedValue,
} from '@client/new/helpers/types';

import { getDispatchers } from '@client/new/helpers/types-utils';
import { useState } from 'react';
import { ChallengeFormVM, useChallengeFormVM } from './ChallengeForm.viewModel';

import { useChallengesModel } from '../model/Challenges.model';
import { ChallengeForm, emptyChallenge } from './ChallengeForm.schema';
import { LiteChallenge } from '../model/Challenges.types';
import {
  toCreateChallengePayload,
  toChallengeForm,
  toChallengesList,
  toChallengeVM,
  toUpdateChallengePayload,
  SortableColumn,
} from './Challenges.mapper';
import { RowType } from '../view/components/common/ContentTable';
import { Sorting, useSorting } from '../view/components/common/useSorting';
import { trackSessionEvent } from '@services/session-tracker/sessionTracker';

export type ChallengesViewActionMap = {
  CreateChallenge: null;
  EditChallenge: string | null | undefined;
  CloseModal: null;
  Search: string;
};

type ChallengesVMAction = Actions<ChallengesViewActionMap>;
type ModalMode = ChallengesVM['modalMode']['type'];
export type TimeStatus = 'Scheduled' | 'Ongoing' | 'Finished';

export type ChallengeVM = {
  name: string;
  status: TimeStatus;
  challengeId: string;
  submissionURL: string;
  registration: { status: TimeStatus; desc: string };
  submissionsCount: number;
  completion: { status: TimeStatus; desc: string };
  onShowSettings?: F0;
  onEdit?: F0;
  onNewChallenge?: F0;
};
export type ChallengesDispatchers = Dispatchers<ChallengesViewActionMap>;
export type ChallengesVM = {
  modalMode: TypedValue<'edit' | 'create', ChallengeFormVM> | { type: 'none' };
  challengesList: Array<RowType>;
  challengesCnt: number;
  ongoingChallengeVM: ChallengeVM | null;
  searchText: string;
  sorting: Sorting<SortableColumn>;
} & ChallengesDispatchers;

export const useChallengesVM = (programId: number): Async<ChallengesVM> => {
  const [modalModeType, setModalModeType] = useState<ModalMode>('none');
  const [selectedChallenge, setSelectedChallenge] =
    useState<LiteChallenge | null>(null);
  const [searchText, setSearchText] = useState('');
  const model = useChallengesModel(programId);
  const sorting = useSorting<SortableColumn>();

  const createChallenge = (data: ChallengeForm) => {
    if (model.type !== 'fetched') return Promise.reject('Model not fetched');

    return model.value.createChallenge(
      toCreateChallengePayload({ ...data, programId }),
    );
  };

  const updateChallenge = (data: ChallengeForm) => {
    if (model.type !== 'fetched' || !selectedChallenge)
      return Promise.reject('Model not fetched');

    return model.value.updateChallenge(
      toUpdateChallengePayload({
        ...data,
        challengeId: selectedChallenge.challengeId,
      }),
    );
  };
  const _dispatch = (a: ChallengesVMAction) => {
    trackSessionEvent('ViewAction', a);
    if (model.type !== 'fetched') return;
    switch (a.type) {
      case 'CloseModal':
        return setModalModeType('none');

      case 'CreateChallenge':
        return setModalModeType('create');

      case 'EditChallenge': {
        const c =
          model.value.challenges.find((c) => c.challengeId === a.payload) ||
          null;
        setSelectedChallenge(c);
        setModalModeType('edit');
        return;
      }
      case 'Search':
        return setSearchText(a.payload);
    }
  };
  const dispatchers = getDispatchers<ChallengesViewActionMap>(_dispatch);

  const newChallengeFormVM = useChallengeFormVM({
    data: emptyChallenge,
    onSubmit: createChallenge,
    challengesVMDispatch: dispatchers.dispatch,
    model,
    delta: {
      name: model.type === 'fetched' ? `${model.value.name} Challenge` : '',
    },
  });

  const editChallengeFormVM = useChallengeFormVM({
    data: selectedChallenge
      ? toChallengeForm(selectedChallenge)
      : emptyChallenge,
    challengeId: selectedChallenge?.challengeId,
    onSubmit: updateChallenge,
    challengesVMDispatch: dispatchers.dispatch,
    model,
  });

  const toModalMode = (): ChallengesVM['modalMode'] => {
    switch (modalModeType) {
      case 'create':
        return { type: 'create', value: newChallengeFormVM };
      case 'edit':
        return { type: modalModeType, value: editChallengeFormVM };
      default:
        return { type: modalModeType };
    }
  };

  if (model.type !== 'fetched') return model;
  const challengesList = toChallengesList({
    role: 'editable',
    cs: model.value.challenges.filter((c) =>
      c.name.toLocaleLowerCase().includes(searchText.toLocaleLowerCase()),
    ),
    onSelectChallenge: dispatchers.lazyDispatch('EditChallenge'),
    sorting,
  });

  const ongoingChallengeVM = toChallengeVM(model.value, dispatchers);

  return toAsyncFetched({
    challengesCnt: model.value.challenges.length,
    modalMode: toModalMode(),
    ongoingChallengeVM,
    challengesList,
    searchText,
    sorting,
    ...dispatchers,
  });
};
